import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useField, useFormikContext} from "formik";
import {debounce} from "lodash";
import PropTypes from "prop-types";

import usePagination from "../../utilities/hooks/usePagination";
import {KEYBOARD_CODES} from "../../utilities/keyboardCodes";
import QuestionItem from "../../utilities/QuestionItem";
import ToolTip from "../../utilities/ToolTip";
import useRoutePermissions, {
  UI_PERMISSION_TYPE,
} from "../adminForms/Ba3aSettings/tabs/PositionsSettings/useRoutePermissions";

import useInputsTooltip from "./hook/InputsTooltipService";

//Select field that has pagination for options, search is realized via refetch with different search param using onFetchForOptions function
export const SelectWithPagination = ({
  label,
  width = "100%",
  id,
  labelWrap,
  options, //{value:smth, title:smth}
  makeEffect = null, /// функция (не обязательная) которая должна сработать когда происходит выбор из выпадающего списка
  question, // пояснение к полю
  minFilterLength = 3, // минимальная длина строки для фильтрации поля
  errorField,
  labelwidth = "",
  optionContainerWidth, // ширина появляющегося списка опций
  disabled = false,
  selectOnly = false,
  inputClassName,
  generalClassName = "",
  labelClassName = "",
  optionsAbove = false,
  optionsLeft = false,
  style,
  textMode = false,
  required,
  onBlur = () => {},
  placeholder = "",
  tooltipMessage,
  bottomTooltip = false,
  onFetchForOptions = () => {}, //function with fetch for select options ({page, changePage, search, signal})
  uiPermissionType = UI_PERMISSION_TYPE.WRITABLE,
  ...props
}) => {
  const {t} = useTranslation();
  const [field, meta, helpers] = useField(props);
  const {value} = field;
  const {initialValues, errors, submitCount, setValues} = useFormikContext();

  const {isDisabledByRoutePermission} = useRoutePermissions({uiPermissionType});

  const {tooltipShown, onMouseEnter, onMouseLeave} = useInputsTooltip(field);
  const {page, setPage, onPaginate, changePage} = usePagination(); //options pagination

  const [textValue, setTextValue] = useState(initialValues?.[props?.name] ?? "");
  const [currentOption, setCurrentOption] = useState(null);
  const [showOptions, setShowOptions] = useState(false);
  const [filter, setFilter] = useState("");
  const [filteredOptions, setFilteredOptions] = useState([]);
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(null);

  const optionsListRef = useRef();
  const optionsItemRef = useRef();
  const optionsListContainerRef = useRef();
  const inputRef = useRef();

  const optionsLength = filteredOptions.length;
  const isInputFocused = document.activeElement === inputRef.current;

  useEffect(() => {
    const abortController = new AbortController();

    onFetchForOptions({page, changePage, search: textValue, signal: abortController.signal, ...props});

    return () => {
      abortController.abort();
    };
  }, [page?.currentPage, Object.values(props).join(""), textValue]);

  const labelText = label
    ? label?.[label?.length - 1] === ":"
      ? required
        ? t(label) + "*"
        : t(label)
      : (required ? t(label) + "*" : t(label)) + ":"
    : "";
  useEffect(() => {
    if (!showOptions) {
      setFocusedOptionIndex(null);
    }
  }, [showOptions]);

  function onOptionsListKeyDown(e) {
    const currentOptionsListRef = optionsListRef.current;
    const optionHeight = optionsItemRef.current?.offsetHeight;
    const scrollBottomValue = optionHeight + 0.5692;
    const scrollTopValue = optionHeight - 0.1652;

    switch (e.code) {
      case KEYBOARD_CODES.TAB:
        if (showOptions) setShowOptions(false);
        if (!currentOption) {
          helpers.setValue(undefined);
        }
        break;
      case KEYBOARD_CODES.ENTER:
        if (focusedOptionIndex !== null) {
          handleSelectOption(filteredOptions[focusedOptionIndex]);
        }
        break;

      case KEYBOARD_CODES.ARROW_UP:
        if (showOptions) {
          if (focusedOptionIndex !== null) {
            setFocusedOptionIndex(prev => (prev !== 0 ? --prev : optionsLength - 1));
            currentOptionsListRef.scrollTop =
              focusedOptionIndex !== 0
                ? currentOptionsListRef.scrollTop - scrollTopValue
                : currentOptionsListRef.scrollHeight;
          } else {
            setFocusedOptionIndex(optionsLength - 1);
            currentOptionsListRef.scrollTop = currentOptionsListRef.scrollHeight;
          }
        } else {
          setShowOptions(true);
        }
        break;
      case KEYBOARD_CODES.ARROW_DOWN:
        if (showOptions) {
          if (focusedOptionIndex !== null && showOptions) {
            setFocusedOptionIndex(prev => (prev !== optionsLength - 1 ? ++prev : 0));
            currentOptionsListRef.scrollTop =
              focusedOptionIndex !== optionsLength - 1 ? currentOptionsListRef.scrollTop + scrollBottomValue : 0;
          } else {
            setFocusedOptionIndex(0);
            currentOptionsListRef.scrollTop = 0;
          }
        } else {
          setShowOptions(true);
        }
        break;
    }
  }

  ///фильтрация
  const updateFilterQuery = filterStr => {
    if (filterStr && filterStr.length >= minFilterLength) {
      setFilter(filterStr);
    } else if (filterStr === "") {
      setFilter("");
    } else {
      setFilter("");
    }
  };

  const delayedFilter = debounce(updateFilterQuery, 500);

  useEffect(() => {
    const filtered = options.filter(option => {
      if (!option?.title || option.title == "") {
        return false;
      }
      let words = option.title.split(" ");
      return words.some(word => word.toLowerCase().includes(filter.toLowerCase()));
    });
    setFilteredOptions(filtered);

    if (filter !== "" && !showOptions && filtered.length > 0 && filtered.length !== options.length) {
      openOptions();
    }
  }, [filter, JSON.stringify(options)]);

  const timeout = useRef();

  const handleSelectOption = option => {
    clearTimeout(timeout.current);
    setShowOptions(false);
    setFilter("");
    setPage(prev => ({...prev, currentPage: 1}));

    helpers.setValue(textMode ? option.title : option.value);
    setCurrentOption(option);
    setTextValue(option.title);
    if (makeEffect) makeEffect(option);
  };

  const optionList = useMemo(() => {
    return filteredOptions.map((option, i) => {
      var {title} = option;
      const isSelected = focusedOptionIndex !== null && filteredOptions[focusedOptionIndex]?.title === title;

      return (
        <li
          className={`select_option ${isSelected ? "selected_option" : ""} ${
            option?.disabled ? "select_li_disabled" : ""
          }`}
          key={i}
          onClick={() => {
            if (!option?.disabled) handleSelectOption(option);
          }}
          ref={optionsItemRef}
        >
          {title.length > 32 ? title.slice(0, 32) + "..." : title}
        </li>
      );
    });
  }, [filteredOptions.length, focusedOptionIndex, filteredOptions]);

  useEffect(() => {
    setFilter("");
    valueChange(value);
  }, [options]);

  const openOptions = () => {
    if (!props.readOnly && !isDisabledByRoutePermission) {
      setShowOptions(true);
    }
  };

  const showAllOptions = () => {
    setFilter("");
  };

  useEffect(() => {
    showAllOptions();
  }, [initialValues]);

  const valueChange = value => {
    if (value && value !== "") {
      if (value?.title) {
        setTextValue(value.title);
        if (textMode) helpers.setValue(options.find(option => option.value === value))?.title ?? "";
      } else if (currentOption?.value === value) {
        return;
      } else if (options.length > 0) {
        const option = options?.find(option => option?.value === value);
        if (option) {
          setTextValue(option.title);
          setCurrentOption(option);
        }
      } else if (options?.length < 1) {
        // setTextValue("");
        setFilter("");
        return;
      }
    }
    // else if ((value === "" || !value) && currentOption && currentOption?.value !== value) {
    //   setCurrentOption(null);
    //   setFilter("");
    //   // setTextValue("");
    // }
  };

  useEffect(() => {
    if (!value) {
      // setTextValue("");
      setFilter("");
      setCurrentOption(null);
      // return;
    }
    if (textMode) setTextValue(value);

    //очищать текст инпутов не в фокусе при пустом value формика
    if (!showOptions && !currentOption?.value && !isInputFocused) {
      setTextValue("");
    }
    // if (!isInputFocused && showOptions) setShowOptions(false);
    valueChange(value);
  }, [value, currentOption?.value, showOptions, isInputFocused]);

  useEffect(() => {
    if (textMode) {
      setValues(prev => ({...prev, [props.name]: textValue}));
    }
  }, [textValue, textMode]);

  const onInputChange = e => {
    const currentText = e.target.value;
    setTextValue(currentText);
    delayedFilter(e.target.value);

    if (!showOptions) setShowOptions(true); //показывать опции (если не показываются) при вводе
    if (
      page !== 1
      // && currentText?.length > 2
    )
      setPage(1); //при вводе начиная от 3 символов (изменении фильтра, фильтр от 3 символов) страница всегда с 1 начинается

    if (currentText && currentText !== "") {
      const option = options.find(option => option?.title === currentText);
      if (option) {
        helpers.setValue(textMode ? currentText : option.value);
        setCurrentOption(option);
      } else if (value !== null && value !== "") {
        helpers.setValue(null);
        if (makeEffect) makeEffect(null);
        setCurrentOption(null);
        // updateFilterQuery("");
        // setTextValue("");
      }
    } else if (value !== null && value !== "") {
      {
        // updateFilterQuery("");
        // setTextValue("");
        helpers.setValue(null);
        if (makeEffect) makeEffect(null);
        setCurrentOption(null);
      }
    }
  };
  // const onInputChangeDebounced = debounce(onInputChange, 500);

  const debouncedPagination = debounce(onPaginate, 1000);

  const handleScroll = () => {
    const {scrollTop, clientHeight, scrollHeight} = optionsListContainerRef.current;
    if (scrollTop + clientHeight > scrollHeight - 5) {
      debouncedPagination();
    }
  };

  return (
    <ToolTip title={typeof tooltipMessage === "string" ? t(tooltipMessage) : tooltipMessage} bottom={bottomTooltip}>
      <div
        className={`project_input select ${generalClassName}  ${labelWrap ? "" : "labelWrap"}`}
        id={id}
        style={style}
      >
        <>
          {label && (
            <label
              htmlFor={props.name}
              style={{width: labelwidth, whiteSpace: "pre"}}
              className={`${props.autolabel ? "autolabel" : ""} ${labelClassName}`}
            >
              {labelText}
            </label>
          )}
          <div
            style={{display: "flex", position: "relative", width: width, maxHeight: "100px"}}
            onMouseLeave={() =>
              setTimeout(() => {
                setShowOptions(false);
                if (!currentOption) {
                  helpers.setValue(undefined);
                }
              }, 100)
            }
          >
            <input
              className={inputClassName}
              readOnly={props.readOnly || selectOnly || isDisabledByRoutePermission}
              value={textValue ?? ""}
              autoComplete="off"
              style={{
                width: "100%",
                ...(props.readOnly || disabled || isDisabledByRoutePermission
                  ? {cursor: "default"}
                  : {background: "white"}),
              }}
              onClick={() => {
                openOptions();
              }}
              onChange={e => {
                // setTextValue(e.target.value);
                onInputChange(e);
                // delayedFilter(e.target.value);
                // onInputChangeDebounced(e);
              }}
              onBlur={e => onBlur({e, setTextValue})}
              disabled={disabled}
              onKeyDown={onOptionsListKeyDown}
              onMouseEnter={question ? onMouseEnter : null}
              onMouseLeave={question ? onMouseLeave : null}
              placeholder={t(placeholder)}
              ref={inputRef}
            />
            {meta.error && (meta.touched || submitCount > 0) && !props.readOnly && !isDisabledByRoutePermission ? (
              <div className="error">{meta.error}</div>
            ) : null}
            {errors[errorField] && meta.touched && submitCount > 0 ? (
              <div className="error">{errors[errorField]}</div>
            ) : null}
            {!props.readOnly && !isDisabledByRoutePermission && (
              <div
                className={`select__list_button select__list_button-responsive ${disabled ? "disable" : ""}`}
                onClick={() => {
                  if (disabled) return;
                  if (!props.readOnly && !isDisabledByRoutePermission) {
                    showAllOptions();
                    setShowOptions(i => (i = !i));
                  }
                }}
              >
                ▼
              </div>
            )}

            {showOptions && (
              <div
                className={`select__list disable ${optionsAbove ? "select__list--above" : ""} ${
                  optionsLeft ? "select__list--left" : ""
                }`}
                ref={optionsListContainerRef}
                onKeyDown={onOptionsListKeyDown}
                tabIndex="0"
                style={{width: optionContainerWidth}}
              >
                <div className="optionListHeader">
                  {optionList.length ? t("Choose an option:") : t("No such option found")}
                </div>
                <ul ref={optionsListRef} className="fcCol" style={{alignItems: "stretch"}} onScroll={handleScroll}>
                  {optionList}
                </ul>
              </div>
            )}
            {question ? <QuestionItem title={question} tooltipShown={tooltipShown} /> : null}
          </div>
        </>
      </div>
    </ToolTip>
  );
};

SelectWithPagination.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.node]),
  width: PropTypes.string,
  question: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  props: PropTypes.array,
  id: PropTypes.string,
  options: PropTypes.array,
  minFilterLength: PropTypes.number,
  errorField: PropTypes.string,
  disabled: PropTypes.bool,
  labelClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  optionsAbove: PropTypes.bool,
  textMode: PropTypes.bool,
  placeholder: PropTypes.string,
  onFetchForOptions: PropTypes.func.isRequired,
  optionContainerWidth: PropTypes.string,
};
